import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icons, Inform, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table/Table';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm/FileTypeSelectionForm';

import { useMutateExportAcquiredAssets } from '@hooks/assets/useMutateExportAcquiredAssets';
import { useMutateStopRentingAsset } from '@hooks/assets/useMutateStopRentingAsset';
import { useQueryAcquiredAssetOwners } from '@hooks/assets/useQueryAcquiredAssetOwners';
import { useQueryAcquiredAssets } from '@hooks/assets/useQueryAcquiredAssets';
import { useQueryAssetSubTypesByType } from '@hooks/assets/useQueryAssetSubTypesByType';
import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';
import { useLinkAndVerifySites } from '@hooks/useLinkAndVerifySite';

import { auth, manageAcquiredAssets } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setAssetOwnerId,
	setAssetSubTypeId,
	setAssetTypeId,
	setDatesOverlapType,
	setEndDateType,
	setFromDate,
	setPage,
	setPageSize,
	setRentStatus,
	setSearchValue,
	setSort,
	setToDate,
} from '@slices/manageAcquiredAssets';

import { setFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { EAdminPanelPageTitles, TMenuActionsTypes } from '@enums/common';

import {
	acquireAssetsExportRadioButtons,
	DEFAULT_END_DATE,
	DEFAULT_START_DATE,
} from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';
import {
	datesOverlapTypeOptions,
	endDateTypeOptions,
	rentStatusOptions,
	searchTooltipText,
} from './constants';
import { noSubTypeOptions } from '@components/routes/AdminPanel/AssetsList/constants';

import { getColumns } from './manageAcquiredAssetsColumns';
import StopRentingAssetForm from './StopRentingAssetForm';

import styles from './ManageAcquiredAssets.module.scss';

const ManageAcquiredAssets = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const {
		accessPermission: {
			networkOperatorTelecomSitesAttributeUpdater,
			networkOperatorTelecomSitesViewer,
			networkOperatorAssetEnquirer,
			assetConsumerAssetEnquirer,
		},
	} = useAppSelector(auth);

	const isLinkAccess = networkOperatorTelecomSitesAttributeUpdater
		&& (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer);
	const isViewSiteAccess = networkOperatorTelecomSitesAttributeUpdater || networkOperatorTelecomSitesViewer;

	const [isInRangeInform, setIsInRangeInform] = useState(false);
	const [isStopRentingAssetModalOpen, setIsStopRentingAssetModalOpen] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
	const [selectedAssetEndDate, setSelectedAssetEndDate] = useState<number | null>(null);

	const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<TSelectOption<string> | null>(null);
	const [selectedSubTypeId, setSelectedSubTypeId] = useState<TSelectOption<string> | null>(null);
	const [selectedAssetOwnerId, setSelectedAssetOwnerId] = useState<TSelectOption<string> | null>(null);
	const [selectedRentStatus, setSelectedRentStatus] = useState<TSelectOption<string> | null>(null);
	const [selectedDatesOverlapType, setSelectedDatesOverlapType] = useState<TSelectOption<string> | null>(null);
	const [selectedEndDateType, setSelectedEndDateType] = useState<TSelectOption<boolean> | null>(null);
	const [selectedAssetsIds, setSelectedAssetsIds] = useState<string[] | [] | null>(null);
	const [activeGroupTableAction, setActiveGroupTableAction] = useState<TMenuActionsTypes>();

	const openStopRentingAssetModal = () => setIsStopRentingAssetModalOpen(true);

	const closeStopRentingAssetModal = () => {
		setIsStopRentingAssetModalOpen(false);

		setSelectedItemId(null);
	};
	const toggleInRangeInform = () => setIsInRangeInform(prevState => !prevState);
	const closeInRangeInform = () => {
		setIsInRangeInform(false);
	};

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		assetTypeId,
		assetSubTypeId,
		assetOwnerId,
		rentStatus,
		datesOverlapType,
		fromDate,
		toDate,
		endDateType,
	} = useAppSelector(manageAcquiredAssets);

	const { assetOwners, isLoadingAssetOwners } = useQueryAcquiredAssetOwners();
	const { acquiredAssets, isLoadingAcquiredAssets, countOfRecords } = useQueryAcquiredAssets();
	const { assetTypesSubtypes, isLoadingAssetTypes } = useQueryAssetTypes({ isAssetTypesSubtypesWithIconUrl: false });
	const { assetSubTypesByType } = useQueryAssetSubTypesByType(assetTypeId);
	const { handleStopRentingAsset } = useMutateStopRentingAsset();
	const { handleExportAcquiredAssets, isLoadingExportAcquiredAssets } = useMutateExportAcquiredAssets();

	const isLoading = (
		isLoadingAssetOwners ||
		isLoadingAcquiredAssets ||
		isLoadingAssetTypes
	);

	const {
		inRadiusStatusesLoading,
	} = useLinkAndVerifySites({
		toggleInRangeInform,
		isLinkAccess,
	});

	const columns = useMemo(() => getColumns({
		navigate,
		isViewSiteAccess,
		setSelectedItemId,
		setSelectedAssetEndDate,
		openStopRentingAssetModal,
	}), [
		navigate,
		isViewSiteAccess,
	]);

	const startDate = useMemo(() => fromDate ? new Date(fromDate) : null, [fromDate]);
	const endDate = useMemo(() => toDate ? new Date(toDate) : null, [toDate]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const onChangeFromDate = useCallback((value: number | null) => {
		dispatch(setFromDate({ fromDate: value }));
	}, [dispatch]);

	const onChangeToDate = useCallback((value: number | null) => dispatch(setToDate({ toDate: value })), [dispatch]);

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const handleExport = (assetsIds: Array<string>, actionId: TMenuActionsTypes) => {
		setSelectedAssetsIds(assetsIds);
		setActiveGroupTableAction(actionId);
		toggleFileTypeSelectionModal();
	};

	const closeExportModal = () => {
		setIsOpenFileTypeSelectionModal(false);
	};

	const assetSubTypesForFilter = useMemo(() => {
		return assetSubTypesByType?.length ? [...assetSubTypesByType, noSubTypeOptions] : [];
	}, [assetSubTypesByType]);

	const filters: Array<TTableFilter> = useMemo(() => [
		{
			name: 'assetType',
			placeholder: 'Asset Type',
			option: selectedAssetTypeId,
			options: assetTypesSubtypes,
			width: '200px',
			setState: setSelectedAssetTypeId,
			defaultValue: assetTypeId,
		},
		{
			name: 'assetSubType',
			placeholder: 'Subtype',
			option: selectedSubTypeId,
			options: assetSubTypesForFilter,
			minWidth: '200px',
			setState: setSelectedSubTypeId,
			defaultValue: assetSubTypeId,
			disabled: !selectedAssetTypeId,
		},
		{
			name: 'assetOwnerId',
			placeholder: 'Asset Owner',
			option: selectedAssetOwnerId,
			options: assetOwners,
			width: '200px',
			setState: setSelectedAssetOwnerId,
			defaultValue: assetOwnerId,
		},
		{
			name: 'rentStatus',
			placeholder: 'Rental Status',
			option: selectedRentStatus,
			options: rentStatusOptions,
			minWidth: '200px',
			setState: setSelectedRentStatus,
			defaultValue: rentStatus,
		},
		{
			name: 'datesOverlapType',
			placeholder: 'Dates overlap type',
			option: selectedDatesOverlapType,
			options: datesOverlapTypeOptions,
			minWidth: '200px',
			setState: setSelectedDatesOverlapType,
			defaultValue: datesOverlapType,
			isFilterStyle: false,
		},
	], [
		selectedAssetTypeId,
		selectedSubTypeId,
		assetTypesSubtypes,
		assetTypeId,
		assetSubTypeId,
		assetSubTypesForFilter,
		selectedAssetOwnerId,
		assetOwners,
		assetOwnerId,
		selectedRentStatus,
		rentStatus,
		selectedDatesOverlapType,
		datesOverlapType,
	]);

	const dateFilters: Array<ITableDatepickerFilter> = useMemo(() => [
		{
			name: 'from',
			label: 'From',
			handleChange: onChangeFromDate,
			selected: startDate,
		},
		{
			name: 'to',
			label: 'To',
			handleChange: onChangeToDate,
			selected: endDate,
			selectProps: {
				name: 'endDateType',
				placeholder: 'End date type',
				option: selectedEndDateType,
				options: endDateTypeOptions,
				minWidth: '200px',
				setState: setSelectedEndDateType,
				defaultValue: endDateType,
			},
		},
	], [
		startDate,
		endDate,
		onChangeFromDate,
		onChangeToDate,
		endDateType,
		selectedEndDateType,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetTypeId,
			currentValue: assetTypeId,
			setAction: setAssetTypeId,
			fieldName: 'assetTypeId',
			dispatch,
		});

		if (!selectedAssetTypeId || selectedSubTypeId) {
			dispatch(setAssetSubTypeId({ assetSubTypeId: null }));

			setSelectedSubTypeId(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetTypeId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedSubTypeId,
			currentValue: assetSubTypeId,
			setAction: setAssetSubTypeId,
			fieldName: 'assetSubTypeId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSubTypeId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetOwnerId,
			currentValue: assetOwnerId,
			setAction: setAssetOwnerId,
			fieldName: 'assetOwnerId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetOwnerId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedRentStatus,
			currentValue: rentStatus,
			setAction: setRentStatus,
			fieldName: 'rentStatus',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRentStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedDatesOverlapType,
			currentValue: datesOverlapType,
			setAction: setDatesOverlapType,
			fieldName: 'datesOverlapType',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDatesOverlapType]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedEndDateType,
			currentValue: endDateType,
			setAction: setEndDateType,
			fieldName: 'endDateType',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedEndDateType]);

	return (
		<div className={styles.manageAcquiredAssetsContainer}>
			<PageTitle
				icon={Icons.assetsActive()}
				title={EAdminPanelPageTitles.ManageAcquiredAssets}
			/>
			{isOpenFileTypeSelectionModal && (
				<Modal
					isOpen={isOpenFileTypeSelectionModal}
					onClose={toggleFileTypeSelectionModal}
					title='File extension to export'
					width={MODAL_WIDTH_455}
				>
					<FileTypeSelectionForm
						activeGroupTableAction={activeGroupTableAction}
						closeModals={closeExportModal}
						exportRadioButtonsData={acquireAssetsExportRadioButtons}
						filter={{
							page,
							pageSize,
							searchValue,
							sortFieldName,
							sortDirection,
							assetTypeId,
							assetSubTypeId,
							ownerId: assetOwnerId,
							timeRentType: rentStatus,
							datesOverlapType,
							isActualEndDate: endDateType !== endDateTypeOptions[0].value,
							'range.from': fromDate || DEFAULT_START_DATE,
							'range.to': toDate || DEFAULT_END_DATE,
						}}
						handlePreparingExport={handleExportAcquiredAssets}
						isLoading={isLoadingExportAcquiredAssets}
						selectedIds={selectedAssetsIds}
						toggleModal={toggleFileTypeSelectionModal}
					/>
				</Modal>
			)}
			{isInRangeInform &&
				<Modal
					isOpen={isInRangeInform}
					onClose={closeInRangeInform}
					title='Information'
				>
					<Inform
						informText='The Site is located within the linked Asset radius'
						isLoading={inRadiusStatusesLoading}
						onClose={closeInRangeInform}
					/>
				</Modal>
			}
			{isStopRentingAssetModalOpen && selectedItemId &&
				<Modal
					isOpen={isStopRentingAssetModalOpen}
					onClose={closeStopRentingAssetModal}
				>
					<StopRentingAssetForm
						actualEndRentDate={selectedAssetEndDate}
						closeModal={closeStopRentingAssetModal}
						handleStopRentingAsset={handleStopRentingAsset}
						itemId={selectedItemId}
					/>
				</Modal>
			}
			<div className={styles.manageAcquiredAssetsTableContainer}>
				<Table
					columns={columns}
					data={acquiredAssets}
					dateFilters={dateFilters}
					filters={filters}
					handleExport={handleExport}
					isExport
					isLoading={isLoading}
					isOpenFilters={!!assetTypeId}
					isOpenSearch={!!searchValue.length}
					isPinColumns
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
				/>
			</div>
		</div>
	);
};

export default memo(ManageAcquiredAssets);
